import React, { useState } from 'react';
import axios from 'axios';
import { useQuery } from 'react-query';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  XCircleIcon,
  BanknotesIcon,
  ArrowTrendingUpIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
  CalculatorIcon,
  CurrencyDollarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilSquareIcon
} from '@heroicons/react/24/outline';
import {
  policyAPI,
  requestAPI,
  requestTypeAPI,
  reasonsAPI,
  formatCurrency,
  getStatusColor,
  getStatusLabel
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './UI/LoadingSpinner';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const generateSavingsStatementPdfForData = async (savings, years, operationsByYear) => {
  if (!savings) return;

  try {
    const doc = new jsPDF('p', 'mm', 'a4');

    // Helper pour les montants (ASCII uniquement pour éviter les problèmes d'encodage PDF)
    const formatAmount = (value) => {
      const num = Number(value || 0);
      if (Number.isNaN(num)) return '0';
      const rounded = Math.round(num).toString();
      return rounded.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    };

    // Ajouter le logo si disponible
    const addLogo = () => new Promise((resolve) => {
      const img = new Image();
      img.src = '/assets/logo-fopam-v2.png';
      img.onload = () => {
        try {
          doc.addImage(img, 'PNG', 15, 10, 22, 22);
        } catch (_) {
          // On ignore une éventuelle erreur d'image pour ne pas bloquer la génération
        }
        resolve();
      };
      img.onerror = () => resolve();
    });

    await addLogo();

    // En-tête texte
    doc.setFontSize(18);
    doc.setTextColor(25, 25, 25);
    doc.text('FOPAM+', 42, 20);

    doc.setFontSize(11);
    doc.setTextColor(90, 90, 90);
    doc.text("Relevé de compte d'épargne", 42, 27);

    // Informations de la police / mutualiste
    let y = 40;
    const policyInfoLines = [
      `Police : ${savings.policy.policyNumber}`,
      `Compte d'épargne : ${savings.savingsAccount.accountNumber}`,
      `Titulaire : ${savings.policy.mutualiste.nom} ${savings.policy.mutualiste.prenoms}`
    ];

    // On n'affiche pas la ligne de profil dans le PDF pour plus de sobriété

    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    policyInfoLines.forEach((line) => {
      doc.text(line, 15, y);
      y += 5;
    });

    // Résumé des montants
    y += 3;
    doc.setFontSize(11);
    doc.setTextColor(25, 25, 25);
    doc.text('Synthèse des montants', 15, y);
    y += 4;

    const summaryRows = [
      ['Cotisations totales', `${formatAmount(savings.totaux.cotisations)} FCFA`],
      ['Intérêts gagnés', `${formatAmount(savings.totaux.interets)} FCFA`],
      ['Rachats', `${formatAmount(savings.totaux.rachats)} FCFA`],
      ['Pénalités', `${formatAmount(savings.totaux.penalites)} FCFA`],
      ['Solde actuel', `${formatAmount(savings.totaux.soldeActuel)} FCFA`]
    ];

    autoTable(doc, {
      startY: y,
      head: [['Indicateur', 'Montant']],
      body: summaryRows,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [30, 64, 175], halign: 'center', fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 248, 255] },
      columnStyles: {
        1: { halign: 'right' }
      },
      didParseCell: (data) => {
        // Mettre en gras la ligne "Solde actuel" (dernière ligne du corps)
        if (data.section === 'body' && data.row.index === summaryRows.length - 1) {
          data.cell.styles.fontStyle = 'bold';
        }
      }
    });

    // Historique synthétique des opérations
    let currentY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(11);
    doc.setTextColor(25, 25, 25);
    doc.text('Historique synthétique des opérations', 15, currentY);
    currentY += 4;

    // Aplatir les opérations par année en lignes lisibles
    const flatOperations = [];
    years.forEach((year) => {
      const operations = operationsByYear[year] || [];
      operations.forEach((op) => {
        if (op.type === 'COTISATION') {
          flatOperations.push([
            String(year),
            'Cotisations',
            `${op.count} paiement${op.count > 1 ? 's' : ''}`,
            `${formatAmount(op.montant)} FCFA`
          ]);
        } else if (op.type === 'INTERET') {
          flatOperations.push([
            String(year),
            'Intérêts',
            op.taux != null ? `${op.taux}%` : '',
            `${formatAmount(op.montant)} FCFA`
          ]);
        } else if (op.type === 'RACHAT') {
          flatOperations.push([
            String(year),
            'Rachat',
            op.typeRachat === 'TOTAL' ? 'Rachat total' : 'Rachat partiel',
            `-${formatAmount(op.montantNet ?? op.montantDemande)} FCFA`
          ]);
        }
      });
    });

    if (flatOperations.length > 0) {
      autoTable(doc, {
        startY: currentY,
        head: [['Année', 'Type', 'Détail', 'Montant']],
        body: flatOperations,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [15, 118, 110], halign: 'center', fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [244, 244, 245] },
        columnStyles: {
          3: { halign: 'right' }
        }
      });
    }

    // Pied de page simple
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.setTextColor(130, 130, 130);
    doc.text("Document généré depuis l'espace administration FOPAM+", 15, pageHeight - 10);

    const fileName = `Releve_Epargne_${savings.policy.policyNumber}.pdf`;
    doc.save(fileName);

    toast.success('Relevé PDF téléchargé');
  } catch (error) {
    console.error('Erreur génération PDF relevé épargne:', error);
    toast.error('Erreur lors de la génération du relevé PDF');
  }
};

const SavingsDetailsModal = ({ policy, onClose }) => {
  const { user, isMutualiste } = useAuth();
  const [expandedYears, setExpandedYears] = useState({});
  const [expandedCotisationYears, setExpandedCotisationYears] = useState({});
  const [loadingDuplicataId, setLoadingDuplicataId] = useState(null);
  const [isSimModalOpen, setIsSimModalOpen] = useState(false);
  const [simAmount, setSimAmount] = useState('');
  const [simResult, setSimResult] = useState(null);
  const [simError, setSimError] = useState('');
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [requestType, setRequestType] = useState('PARTIAL');
  const [requestAmount, setRequestAmount] = useState('');
  const [requestReasonId, setRequestReasonId] = useState('');
  const [requestError, setRequestError] = useState('');

  const [isAmountChangeModalOpen, setIsAmountChangeModalOpen] = useState(false);
  const [newContributionAmount, setNewContributionAmount] = useState('');
  const [amountChangeReasonText, setAmountChangeReasonText] = useState('');
  const [amountChangeError, setAmountChangeError] = useState('');
  const [isSubmittingAmountChange, setIsSubmittingAmountChange] = useState(false);

  // Récupérer les détails d'épargne
  const { data: savingsData, isLoading } = useQuery(
    ['policy-savings-details', policy.id],
    () => policyAPI.getPolicySavingsDetails(policy.id),
    {
      enabled: !!policy.id
    }
  );

  const savings = savingsData?.data;

  // Charger les types de demande pour identifier les IDs correspondant aux rachats d'épargne
  const { data: requestTypesData } = useQuery(
    ['request-types'],
    () => requestTypeAPI.getRequestTypes().then(res => res.data),
    {
      enabled: !!savings
    }
  );

  // Charger les motifs de demande rattachés à la table "requests"
  const { data: reasonsData } = useQuery(
    ['request-reasons'],
    () => reasonsAPI.getReasons({ tableRattachement: 'requests', isActive: true }).then(res => res.data),
    {
      enabled: !!savings
    }
  );

  const requestReasons = reasonsData?.data || reasonsData || [];

  // Détection d'une demande de rachat en attente pour cette police
  // On couvre à la fois MUTUALISTE (via getUserRequests) et ADMIN/AGENT/SUPERVISEUR (via getRequests)

  // Requêtes de l'utilisateur courant (utile pour les mutualistes)
  const { data: myRequestsData } = useQuery(
    ['my-requests-withdrawals'],
    () => requestAPI.getUserRequests().then(res => res.data),
    {
      enabled: !!policy?.id && !!savings && !!user && isMutualiste,
    }
  );

  // Requêtes globales filtrées pour les profils ayant accès à /api/requests
  const { data: adminRequestsData } = useQuery(
    ['admin-requests-withdrawals', policy?.id],
    () => requestAPI.getRequests({ policyId: policy.id, status: 'En attente' }),
    {
      enabled: !!policy?.id && !!savings && !!user && !isMutualiste,
    }
  );

  // /requests/user renvoie { data: Request[] }
  const myRequests = Array.isArray(myRequestsData?.data) ? myRequestsData.data : [];

  // /requests renvoie { data: { requests, pagination } }
  const adminRequests = adminRequestsData?.data?.data?.requests || [];

  const allRelevantRequests = isMutualiste ? myRequests : adminRequests;

  const hasPendingWithdrawalRequest = Array.isArray(allRelevantRequests)
    ? allRelevantRequests.some((req) => {
        const typeLabel = req.type?.libelle || req.type;
        const statusLabel = req.status?.libelle || req.status;
        const isWithdrawalType =
          typeLabel === 'Rachat partiel épargne' ||
          typeLabel === 'Rachat total épargne';
        const isPendingStatus = statusLabel === 'En attente' || statusLabel === 'EN_ATTENTE';
        const samePolicy = req.policy?.id
          ? req.policy.id === policy.id
          : req.policyId === policy.id;
        return isWithdrawalType && isPendingStatus && samePolicy;
      })
    : false;

  const hasPendingAmountChangeRequest = Array.isArray(allRelevantRequests)
    ? allRelevantRequests.some((req) => {
        const typeLabel = req.type?.libelle || req.type;
        const statusLabel = req.status?.libelle || req.status;
        const isPendingStatus = statusLabel === 'En attente' || statusLabel === 'EN_ATTENTE';
        const samePolicy = req.policy?.id ? req.policy.id === policy.id : req.policyId === policy.id;
        return typeLabel === "Modification du montant de l'épargne" && isPendingStatus && samePolicy;
      })
    : false;

  // DEBUG: inspection des demandes de rachat pour cette police
  console.log('[SavingsDetailsModal] pending withdrawal check', {
    policyId: policy.id,
    isMutualiste,
    allRelevantRequests,
    hasPendingWithdrawalRequest,
  });

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const dob = new Date(birthDate);
    if (Number.isNaN(dob.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - dob.getFullYear();
    const m = today.getMonth() - dob.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
      age--;
    }
    return age;
  };

  const handleDownloadDuplicata = async (detail) => {
    if (!detail.paymentId || !detail.receiptNumber) {
      toast.error('Aucun reçu disponible pour cette cotisation');
      return;
    }
    setLoadingDuplicataId(`${detail.year}-${detail.month}`);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_URL}/payments/${detail.paymentId}/receipt-duplicate`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `duplicata-${detail.receiptNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Duplicata téléchargé avec succès');
    } catch (error) {
      console.error('Erreur génération duplicata:', error);
      toast.error('Erreur lors de la génération du duplicata');
    } finally {
      setLoadingDuplicataId(null);
    }
  };

  const toggleYear = (year) => {
    setExpandedYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }));
  };

  const toggleCotisationYear = (year) => {
    setExpandedCotisationYears(prev => ({
      ...prev,
      [year]: !prev[year]
    }));
  };

  const getOperationIcon = (type) => {
    switch (type) {
      case 'COTISATION':
        return <BanknotesIcon className="h-5 w-5 text-blue-600" />;
      case 'INTERET':
        return <ArrowTrendingUpIcon className="h-5 w-5 text-green-600" />;
      case 'RACHAT':
        return <ArrowDownTrayIcon className="h-5 w-5 text-red-600" />;
      default:
        return <CurrencyDollarIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getOperationLabel = (type) => {
    switch (type) {
      case 'COTISATION':
        return 'Cotisations';
      case 'INTERET':
        return 'Intérêts';
      case 'RACHAT':
        return 'Rachat';
      default:
        return type;
    }
  };

  const getOperationColor = (type) => {
    switch (type) {
      case 'COTISATION':
        return 'bg-blue-50 border-blue-200';
      case 'INTERET':
        return 'bg-green-50 border-green-200';
      case 'RACHAT':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  // Déterminer une date de tri pour chaque opération afin de les ordonner
  // en ordre chronologique décroissant à l'intérieur d'une année donnée.
  const getOperationSortDate = (op) => {
    if (!op) return new Date(0);

    // Pour les rachats, on se base sur la date réelle de rachat
    if (op.type === 'RACHAT' && op.dateRachat) {
      return new Date(op.dateRachat);
    }

    // Pour les intérêts, on se base sur la fin de période
    if (op.type === 'INTERET' && op.periodeFin) {
      return new Date(op.periodeFin);
    }

    // Pour les agrégats de cotisations, utiliser l'année comme repère
    if (op.year) {
      // On place la date au 31/12 de l'année pour que l'agrégat
      // reste cohérent dans le tri si d'autres types ont des dates précises.
      return new Date(Number(op.year), 11, 31);
    }

    // Fallback très conservateur
    return new Date(0);
  };

  // Grouper les opérations par année
  const operationsByYear = savings?.operations.reduce((acc, op) => {
    if (!acc[op.year]) {
      acc[op.year] = [];
    }
    acc[op.year].push(op);
    return acc;
  }, {}) || {};

  // À l'intérieur de chaque année, trier les opérations par date décroissante
  Object.keys(operationsByYear).forEach((year) => {
    operationsByYear[year] = operationsByYear[year]
      .slice()
      .sort((a, b) => getOperationSortDate(b) - getOperationSortDate(a));
  });

  const years = Object.keys(operationsByYear).sort((a, b) => b - a);

  const handleSimulateWithdrawal = () => {
    if (!savings) return;

    const raw = Number(simAmount);
    if (!simAmount || Number.isNaN(raw) || raw <= 0) {
      setSimError('Veuillez saisir un montant valide.');
      setSimResult(null);
      return;
    }

    const balance = Number(savings.savingsAccount.currentBalance || 0);
    if (balance <= 0) {
      setSimError('Aucun solde disponible pour un rachat.');
      setSimResult(null);
      return;
    }

    const offer = savings.policy.offer || {};
    const feeRateConfig = offer.withdrawalFeeRate != null ? Number(offer.withdrawalFeeRate) : 0;
    const minAmount = offer.minWithdrawalAmount != null ? Number(offer.minWithdrawalAmount) : 0;
    const allowPartial = offer.allowPartialWithdrawal !== false;

    let typeRachat = 'PARTIAL';
    let effectiveAmount = raw;

    if (raw >= balance) {
      typeRachat = 'TOTAL';
      effectiveAmount = balance;
    }

    if (typeRachat === 'PARTIAL') {
      if (!allowPartial) {
        setSimError('Les rachats partiels ne sont pas autorisés pour ce produit.');
        setSimResult(null);
        return;
      }

      if (minAmount > 0 && effectiveAmount < minAmount) {
        setSimError(`Le montant minimum de rachat partiel est de ${formatCurrency(minAmount)}.`);
        setSimResult(null);
        return;
      }

      if (effectiveAmount > balance) {
        setSimError('Le montant demandé dépasse le solde disponible.');
        setSimResult(null);
        return;
      }
    }

    const feeRate = typeRachat === 'PARTIAL' ? feeRateConfig : 0;
    const feeAmount = (effectiveAmount * feeRate) / 100;
    const netAmount = effectiveAmount - feeAmount;
    const currentBalance = Number(savings.savingsAccount.currentBalance || 0);
    const remainingBalance = currentBalance - netAmount - feeAmount;

    setSimError('');
    setSimResult({
      typeRachat,
      cotisationsTotales: savings.totaux.cotisations,
      montantDemande: effectiveAmount,
      penalites: feeAmount,
      montantNet: netAmount,
      soldeRestant: remainingBalance,
      feeRate
    });
  };

  const generateSavingsStatementPdf = async () => {
    if (!savings) return;
    await generateSavingsStatementPdfForData(savings, years, operationsByYear);
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
          <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner size="large" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!savings) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            {/* En-tête */}
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-2xl leading-6 font-bold text-gray-900">
                  Détails de la police
                </h3>
                <p className="text-sm text-gray-500 mt-1 flex flex-wrap gap-2">
                  <span className="flex items-center gap-2 flex-wrap">
                    <span>
                      Police {savings.policy.policyNumber}
                    </span>
                    {savings.policy.status && (
                      <span className={`badge badge-${getStatusColor(savings.policy.status?.libelle || savings.policy.status)}`}>
                        {getStatusLabel(savings.policy.status?.libelle || savings.policy.status)}
                      </span>
                    )}
                    {savings.policy.offer?.name && (
                      <span>
                        • {savings.policy.offer.name}
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-2">
                    <span>
                      •{' '}
                      Compte {savings.savingsAccount.accountNumber}
                    </span>
                    {savings.savingsAccount.status && (
                      <span className={`badge badge-${savings.savingsAccount.status === 'ACTIVE' ? 'success' : 'danger'}`}>
                        {savings.savingsAccount.status === 'ACTIVE' ? 'Actif' : 'Fermé'}
                      </span>
                    )}
                  </span>
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {savings.policy.mutualiste.nom} {savings.policy.mutualiste.prenoms}
                  {savings.policy.mutualiste.matricule ? ` • ${savings.policy.mutualiste.matricule}` : ''}
                  {savings.policy.mutualiste.genre?.libelle ? ` • ${savings.policy.mutualiste.genre.libelle}` : ''}
                  {(() => {
                    const age = calculateAge(savings.policy.mutualiste.dateNaissance);
                    return age !== null ? ` • ${age} ans` : '';
                  })()}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Solde actuel - Mis en évidence */}
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl shadow-lg p-6 mb-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-primary-100 text-sm font-medium mb-1">Solde actuel</p>
                  <p className="text-4xl font-bold">{formatCurrency(savings.savingsAccount.currentBalance)}</p>
                </div>
                <div className="text-right">
                  <BanknotesIcon className="h-16 w-16 text-primary-300 opacity-50" />
                </div>
              </div>
            </div>

            {/* Totaux */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <p className="text-xs text-blue-600 font-medium mb-1">Cotisations totales</p>
                <p className="text-lg font-bold text-blue-900">{formatCurrency(savings.totaux.cotisations)}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <p className="text-xs text-green-600 font-medium mb-1">Intérêts gagnés</p>
                <p className="text-lg font-bold text-green-900">{formatCurrency(savings.totaux.interets)}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <p className="text-xs text-red-600 font-medium mb-1">Rachats</p>
                <p className="text-lg font-bold text-red-900">{formatCurrency(savings.totaux.rachats)}</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                <p className="text-xs text-orange-600 font-medium mb-1">Pénalités</p>
                <p className="text-lg font-bold text-orange-900">{formatCurrency(savings.totaux.penalites)}</p>
              </div>
            </div>

            {/* Historique par année */}
            <div className="mb-6 bg-blue-50 rounded-lg p-5">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Historique des opérations</h4>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {years.map(year => {
                  const operations = operationsByYear[year];
                  const isExpanded = expandedYears[year];
                  
                  return (
                    <div key={year} className="border border-gray-200 rounded-lg">
                      <button
                        onClick={() => toggleYear(year)}
                        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-lg font-bold text-gray-900">{year}</span>
                        </div>
                        {isExpanded ? (
                          <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                      
                      {isExpanded && (
                        <div className="border-t border-gray-200 p-4 space-y-3">
                          {operations.map((op, index) => (
                            <div
                              key={`${op.type}-${index}`}
                              className={`border rounded-lg p-4 ${getOperationColor(op.type)}`}
                            >
                              {op.type === 'COTISATION' ? (
                                <div>
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center space-x-3">
                                      {getOperationIcon(op.type)}
                                      <p className="font-semibold text-gray-900">{getOperationLabel(op.type)}</p>
                                    </div>
                                    <p className="text-lg font-bold text-green-700">+{formatCurrency(op.montant)}</p>
                                  </div>
                                  {Array.isArray(op.details) && op.details.length > 0 ? (
                                    <div className="overflow-x-auto rounded-lg">
                                      <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gradient-to-r from-blue-500 to-blue-600">
                                          <tr>
                                            <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Échéance</th>
                                            <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Montant</th>
                                            <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Statut</th>
                                            <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider">Date paiement</th>
                                            <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider">Actions</th>
                                          </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                          {[...op.details].sort((a, b) => a.month - b.month).map((detail, idx) => (
                                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-blue-50'}>
                                              <td className="px-4 py-3 text-sm font-medium text-gray-900 capitalize">
                                                {detail.monthLabel ?? detail.month}
                                              </td>
                                              <td className="px-4 py-3 text-sm font-bold text-blue-600">
                                                {formatCurrency(detail.montant)}
                                              </td>
                                              <td className="px-4 py-3 text-sm">
                                                <span className="badge badge-success">Payée</span>
                                              </td>
                                              <td className="px-4 py-3 text-sm text-gray-600">
                                                {detail.paidAt ? new Date(detail.paidAt).toLocaleDateString('fr-FR') : '-'}
                                              </td>
                                              <td className="px-4 py-3 text-sm text-center">
                                                {detail.paymentId && detail.receiptNumber && (
                                                  <button
                                                    type="button"
                                                    onClick={() => handleDownloadDuplicata(detail)}
                                                    disabled={loadingDuplicataId === `${detail.year}-${detail.month}`}
                                                    className="text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    title="Télécharger le duplicata de reçu"
                                                  >
                                                    {loadingDuplicataId === `${detail.year}-${detail.month}` ? (
                                                      <img src="/assets/logo-fopam-v2.png" alt="" className="h-5 w-5 animate-spin inline" />
                                                    ) : (
                                                      <PrinterIcon className="h-5 w-5 inline" />
                                                    )}
                                                  </button>
                                                )}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <p className="text-sm text-gray-600">{op.count} paiement{op.count > 1 ? 's' : ''}</p>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-start justify-between">
                                  <div className="flex items-center space-x-3">
                                    {getOperationIcon(op.type)}
                                    <div>
                                      <p className="font-semibold text-gray-900">{getOperationLabel(op.type)}</p>

                                      {op.type === 'INTERET' && (
                                        <div className="text-sm text-gray-600 mt-1 space-y-1">
                                          <p>Taux: {op.taux}%</p>
                                          <p>Base de calcul: {formatCurrency(op.baseCalcul)}</p>
                                          <p className="text-xs">
                                            Période: {new Date(op.periodeDebut).toLocaleDateString('fr-FR')} au {new Date(op.periodeFin).toLocaleDateString('fr-FR')}
                                          </p>
                                        </div>
                                      )}

                                      {op.type === 'RACHAT' && (
                                        <div className="text-sm text-gray-600 mt-1 space-y-1">
                                          <p>Type: {op.typeRachat === 'TOTAL' ? 'Rachat total' : 'Rachat partiel'}</p>
                                          <p>N° {op.numeroRachat}</p>
                                          {op.penalites > 0 && (
                                            <p className="text-orange-600 font-medium">
                                              Pénalités: {formatCurrency(op.penalites)}
                                            </p>
                                          )}
                                          <p className="text-xs">
                                            Date: {new Date(op.dateRachat).toLocaleDateString('fr-FR')}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div className="text-right">
                                    <p className={`text-lg font-bold ${op.type === 'INTERET' ? 'text-green-700' : 'text-red-700'}`}>
                                      {op.type === 'RACHAT' ? '-' : '+'}{formatCurrency(
                                        op.type === 'RACHAT' ? op.montantApprouve : op.montant
                                      )}
                                    </p>
                                    {op.type === 'RACHAT' && (
                                      <p className="text-sm text-gray-600 mt-1">
                                        Net payé: {formatCurrency(op.montantNet)}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {isSimModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">Simulation de rachat</h4>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIsSimModalOpen(false);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XCircleIcon className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Montant à racheter
                      </label>
                      <div className="mt-1 flex items-center space-x-3">
                        <input
                          type="number"
                          className="flex-1 rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                          value={simAmount}
                          onChange={(e) => {
                            setSimAmount(e.target.value);
                            setSimError('');
                          }}
                          min="0"
                        />
                        <button
                          type="button"
                          onClick={handleSimulateWithdrawal}
                          className="btn-primary px-4 py-2 text-sm whitespace-nowrap"
                        >
                          Calculer la simulation
                        </button>
                      </div>
                      {simError && (
                        <p className="mt-2 text-sm text-red-600">{simError}</p>
                      )}
                    </div>

                    {simResult && (
                      <div className="mt-2 border border-gray-200 rounded-lg p-4 bg-gray-50 text-sm space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Type de rachat simulé</span>
                          <span className="font-semibold text-gray-900">
                            {simResult.typeRachat === 'TOTAL' ? 'Rachat total' : 'Rachat partiel'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Solde actuel</span>
                          <span className="font-semibold text-gray-900">{formatCurrency(savings.savingsAccount.currentBalance)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Montant demandé</span>
                          <span className="font-semibold text-gray-900">{formatCurrency(simResult.montantDemande)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">
                            Pénalités estimées{simResult.feeRate ? ` (${simResult.feeRate} %)` : ''}
                          </span>
                          <span className="font-semibold text-orange-700">{formatCurrency(simResult.penalites)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Montant à recevoir estimé</span>
                          <span className="font-semibold text-green-700">{formatCurrency(simResult.montantNet)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Solde estimé après rachat</span>
                          <span className="font-semibold text-gray-900">{formatCurrency(simResult.soldeRestant)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {isRequestModalOpen && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">Demande de rachat</h4>
                      <p className="text-sm text-gray-500 mt-1">
                        Veuillez saisir les informations de votre demande de rachat d'épargne.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setIsRequestModalOpen(false);
                        setRequestError('');
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XCircleIcon className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type de rachat
                      </label>
                      <select
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                        value={requestType}
                        onChange={(e) => setRequestType(e.target.value)}
                      >
                        <option value="PARTIAL">Rachat partiel</option>
                        <option value="TOTAL">Rachat total</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Montant demandé (laisser vide pour rachat total)
                      </label>
                      <input
                        type="number"
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                        value={requestAmount}
                        onChange={(e) => {
                          setRequestAmount(e.target.value);
                          setRequestError('');
                        }}
                        min="0"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Motif de la demande
                      </label>
                      <select
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 text-sm"
                        value={requestReasonId}
                        onChange={(e) => {
                          setRequestReasonId(e.target.value);
                          setRequestError('');
                        }}
                      >
                        <option value="">Sélectionner un motif</option>
                        {Array.isArray(requestReasons) && requestReasons.map((reason) => (
                          <option key={reason.id} value={reason.id}>
                            {reason.libelle}
                          </option>
                        ))}
                      </select>
                    </div>

                    {requestError && (
                      <p className="text-sm text-red-600">{requestError}</p>
                    )}

                    <div className="flex justify-end space-x-2 mt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setIsRequestModalOpen(false);
                          setRequestError('');
                          setRequestReasonId('');
                        }}
                        className="btn-secondary px-4 py-2 text-sm"
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            if (!savings) {
                              toast.error('Données d\'épargne indisponibles');
                              return;
                            }

                            const requestTypes = requestTypesData?.data || requestTypesData || [];
                            const typeLabel = requestType === 'TOTAL' ? 'Rachat total épargne' : 'Rachat partiel épargne';
                            const matchingType = Array.isArray(requestTypes)
                              ? requestTypes.find(rt => rt.libelle === typeLabel)
                              : null;

                            if (!matchingType) {
                              setRequestError('Type de demande de rachat introuvable.');
                              return;
                            }

                            if (!requestReasonId) {
                              setRequestError('Veuillez sélectionner un motif de demande.');
                              return;
                            }

                            const selectedReason = Array.isArray(requestReasons)
                              ? requestReasons.find(r => r.id === requestReasonId)
                              : null;

                            let amount = undefined;
                            if (requestType === 'PARTIAL') {
                              const raw = Number(requestAmount);
                              if (!requestAmount || Number.isNaN(raw) || raw <= 0) {
                                setRequestError('Veuillez saisir un montant valide pour un rachat partiel.');
                                return;
                              }

                              const balance = Number(savings.savingsAccount.currentBalance || 0);
                              if (raw > balance) {
                                setRequestError('Le montant demandé dépasse le solde disponible.');
                                return;
                              }

                              amount = raw;
                            }

                            await requestAPI.createRequest({
                              policyId: policy.id,
                              typeId: matchingType.id,
                              reasonId: requestReasonId,
                              reasonText: selectedReason?.libelle || `Demande de ${typeLabel.toLowerCase()}`,
                              amount,
                              notes: simResult
                                ? `Simulation: net=${simResult.montantNet}, penalites=${simResult.penalites}, soldeApres=${simResult.soldeRestant}`
                                : undefined
                            });

                            toast.success('Demande de rachat créée et mise en attente de validation.');
                            setIsRequestModalOpen(false);
                            setRequestError('');
                            setRequestReasonId('');
                          } catch (error) {
                            console.error('Erreur lors de la création de la demande de rachat:', error);
                            setRequestError('Erreur lors de la création de la demande de rachat.');
                          }
                        }}
                        className="btn-primary px-4 py-2 text-sm"
                      >
                        Confirmer la demande
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Modal de modification du montant de cotisation */}
            {isAmountChangeModalOpen && (
              <div className="fixed inset-0 z-[60] flex items-center justify-center">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => { setIsAmountChangeModalOpen(false); setAmountChangeError(''); }} />
                <div className="relative bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4 z-10">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Modifier le montant de la cotisation</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs text-gray-500">Montant actuel</p>
                        <p className="text-lg font-bold text-gray-900">{formatCurrency(parseFloat(policy.monthlyAmount || 0))}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Nouveau montant mensuel <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          min="1"
                          className="form-input w-full"
                          value={newContributionAmount}
                          onChange={(e) => { setNewContributionAmount(e.target.value); setAmountChangeError(''); }}
                          placeholder="Ex: 35000"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Motif de la demande <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        rows={3}
                        className="form-input w-full"
                        value={amountChangeReasonText}
                        onChange={(e) => { setAmountChangeReasonText(e.target.value); setAmountChangeError(''); }}
                        placeholder="Expliquez la raison de cette modification..."
                      />
                    </div>
                    {amountChangeError && (
                      <p className="text-sm text-red-600">{amountChangeError}</p>
                    )}
                    <div className="flex justify-end space-x-2">
                      <button type="button" onClick={() => { setIsAmountChangeModalOpen(false); setAmountChangeError(''); }}
                        className="btn-secondary px-4 py-2 text-sm">Annuler</button>
                      <button
                        type="button"
                        disabled={isSubmittingAmountChange}
                        className="btn-primary px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={async () => {
                          try {
                            const requestTypes = requestTypesData?.data || requestTypesData || [];
                            const matchingType = Array.isArray(requestTypes)
                              ? requestTypes.find(rt => rt.libelle === "Modification du montant de l'épargne")
                              : null;
                            if (!matchingType) { setAmountChangeError('Type de demande introuvable. Contactez un administrateur.'); return; }
                            const newAmt = Number(newContributionAmount);
                            if (!newContributionAmount || Number.isNaN(newAmt) || newAmt <= 0) {
                              setAmountChangeError('Veuillez saisir un montant valide (> 0).');
                              return;
                            }
                            if (newAmt === parseFloat(policy.monthlyAmount || 0)) {
                              setAmountChangeError('Le nouveau montant doit être différent du montant actuel.');
                              return;
                            }
                            if (!amountChangeReasonText.trim()) {
                              setAmountChangeError('Veuillez saisir un motif.');
                              return;
                            }
                            setIsSubmittingAmountChange(true);
                            await requestAPI.createRequest({
                              policyId: policy.id,
                              typeId: matchingType.id,
                              reasonText: amountChangeReasonText.trim(),
                              metadata: {
                                currentAmount: parseFloat(policy.monthlyAmount || 0),
                                newAmount: newAmt
                              }
                            });
                            toast.success('Demande de modification du montant soumise avec succès.');
                            setIsAmountChangeModalOpen(false);
                            setNewContributionAmount('');
                            setAmountChangeReasonText('');
                            setAmountChangeError('');
                          } catch (error) {
                            console.error('Erreur création demande modification montant:', error);
                            setAmountChangeError('Erreur lors de la création de la demande.');
                          } finally {
                            setIsSubmittingAmountChange(false);
                          }
                        }}
                      >
                        {isSubmittingAmountChange ? 'Envoi...' : 'Confirmer la demande'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setSimAmount('');
                  setSimResult(null);
                  setSimError('');
                  setIsSimModalOpen(true);
                }}
                className="flex-1 btn-secondary flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={hasPendingWithdrawalRequest || savings.savingsAccount.status !== 'ACTIVE'}
              >
                <CalculatorIcon className="h-5 w-5 mr-2" />
                Simuler un rachat
              </button>
              <button
                onClick={() => {
                  if (!savings) {
                    toast.error('Données d\'épargne indisponibles');
                    return;
                  }

                  // Ne plus pré-remplir depuis la simulation : toujours partir d'un formulaire vierge
                  setRequestType('PARTIAL');
                  setRequestAmount('');
                  setRequestReasonId('');
                  setRequestError('');
                  setIsRequestModalOpen(true);
                }}
                className="flex-1 btn-primary flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={hasPendingWithdrawalRequest || savings.savingsAccount.status !== 'ACTIVE'}
              >
                <BanknotesIcon className="h-5 w-5 mr-2" />
                Demander un rachat
              </button>
              <button
                onClick={() => {
                  setNewContributionAmount('');
                  setAmountChangeReasonText('');
                  setAmountChangeError('');
                  setIsAmountChangeModalOpen(true);
                }}
                className="flex-1 btn-warning flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={hasPendingAmountChangeRequest || savings.savingsAccount.status !== 'ACTIVE'}
                title={hasPendingAmountChangeRequest ? 'Une demande de modification est déjà en attente' : ''}
              >
                <PencilSquareIcon className="h-5 w-5 mr-2" />
                Modifier la cotisation
              </button>
              <button
                onClick={generateSavingsStatementPdf}
                disabled={isLoading || !savings || !Array.isArray(years) || years.length === 0}
                className="flex-1 btn-success flex items-center justify-center"
              >
                <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
                Télécharger un relevé
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SavingsDetailsModal;
